/**
 * @file computeBinLayout.ts
 * @description Pure layout utility for computing auto-layout positions of items
 * inside a bin. Supports row (single horizontal), column (single vertical),
 * and grid (flow) layout modes.
 *
 * No React or Yjs dependencies — fully unit-testable.
 */

import type { BinLayoutMode } from './binLayoutMode'
import { BIN_HEADER_HEIGHT, BIN_CONTENT_PADDING, BIN_ITEM_GAP } from './canvasPanelConstants'

// ============================================================================
// TYPES
// ============================================================================

export interface BinChildInfo {
  /** Unique node ID (e.g., 'asset-abc', 'job-xyz') */
  id: string
  /** Measured or default width of the child node */
  width: number
  /** Measured or default height of the child node */
  height: number
}

export interface BinLayoutResult {
  /** Map from child node ID to its computed position (relative to the bin node) */
  positions: Map<string, { x: number; y: number }>
  /** Computed bin width to fit all children */
  width: number
  /** Computed bin height to fit all children */
  height: number
}

// ============================================================================
// LAYOUT FUNCTIONS
// ============================================================================

/**
 * Compute auto-layout positions for children inside a bin.
 *
 * @param children - Ordered list of children with dimensions
 * @param layoutMode - How to arrange children: 'row' (single horizontal),
 *   'columns' (single vertical), or 'grid' (flow).
 * @param gapOpenIndex - Optional insertion index where a gap should be opened
 *   (existing items at or after this index shift to the next slot)
 * @param draggedSize - Optional dimensions of the item being dragged into the
 *   gap. Used to size the gap slot accurately for heterogeneous bins. When
 *   omitted, the gap falls back to the dimensions of `children[0]`.
 * @param forcedGridColumns - Optional explicit column count for `grid` mode
 *   (a user-set count from corner-resizing the bin). Overrides the
 *   `computeGridColCount` aspect-ratio heuristic. Clamped to `1..childCount`.
 *   Ignored for non-grid modes.
 * @returns Computed positions for each child and the required bin dimensions
 */
export function computeBinLayout(
  children: BinChildInfo[],
  layoutMode: BinLayoutMode,
  gapOpenIndex?: number | null,
  draggedSize?: { width: number; height: number } | null,
  forcedGridColumns?: number | null,
  layoutWidth?: number,
): BinLayoutResult {
  if (layoutMode === 'grid' && layoutWidth != null && Number.isFinite(layoutWidth) && layoutWidth >= 200) {
    const width = Math.max(layoutWidth, ...children.map(child => child.width + BIN_CONTENT_PADDING * 2))
    if (!children.length) return { positions: new Map(), width, height: BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING * 2 }
    return computeFlowLayout(children, width - BIN_CONTENT_PADDING * 2, gapOpenIndex, draggedSize)
  }
  if (children.length === 0) {
    return {
      positions: new Map(),
      width: 200,
      height: BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING * 2,
    }
  }

  switch (layoutMode) {
    case 'row':
      return computeRowLayout(children, gapOpenIndex, draggedSize)
    case 'columns':
      return computeColumnsLayout(children, gapOpenIndex, draggedSize)
    case 'freeform':
      // No auto-layout — callers should size the bin from stored dimensions and
      // let children render at their own canvasPositions. We return an empty
      // positions map so any caller that forgets to gate gets a no-op rather
      // than a misleading grid layout.
      return {
        positions: new Map(),
        width: 200,
        height: BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING * 2,
      }
    case 'grid':
    default:
      return computeGridLayout(children, gapOpenIndex, draggedSize, forcedGridColumns)
  }
}

// ============================================================================
// ROW LAYOUT
// ============================================================================

/**
 * Single horizontal row — items placed left-to-right.
 */
function computeRowLayout(
  children: BinChildInfo[],
  gapOpenIndex?: number | null,
  draggedSize?: { width: number; height: number } | null,
): BinLayoutResult {
  const positions = new Map<string, { x: number; y: number }>()
  const gapWidth = draggedSize?.width ?? children[0].width
  const gapHeight = draggedSize?.height ?? 0
  const maxH = Math.max(...children.map(c => c.height), gapOpenIndex != null ? gapHeight : 0)

  let cursorX = BIN_CONTENT_PADDING
  const startY = BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING

  for (let i = 0; i < children.length; i++) {
    // Insert gap at the specified index, sized for the dragged item
    if (gapOpenIndex != null && i === gapOpenIndex) {
      cursorX += gapWidth + BIN_ITEM_GAP
    }

    const child = children[i]
    // Top-align within the row: a closed bin or a short card sits at the
    // top of its slot, not floating mid-row.
    positions.set(child.id, { x: cursorX, y: startY })
    cursorX += child.width + BIN_ITEM_GAP
  }

  // If gap is after the last item
  if (gapOpenIndex != null && gapOpenIndex >= children.length) {
    cursorX += gapWidth + BIN_ITEM_GAP
  }

  const totalWidth = cursorX - BIN_ITEM_GAP + BIN_CONTENT_PADDING
  const totalHeight = startY + maxH + BIN_CONTENT_PADDING

  return { positions, width: totalWidth, height: totalHeight }
}

// ============================================================================
// GRID LAYOUT
// ============================================================================

/**
 * Build a list of items (with optional gap placeholder) for flow layout.
 */
function buildSlotList(children: BinChildInfo[], gapOpenIndex?: number | null): (BinChildInfo | null)[] {
  if (gapOpenIndex == null) return [...children]
  const slots: (BinChildInfo | null)[] = []
  let childIdx = 0
  const slotCount = children.length + 1
  for (let s = 0; s < slotCount; s++) {
    if (s === gapOpenIndex) {
      slots.push(null) // gap placeholder
    } else {
      slots.push(children[childIdx++])
    }
  }
  return slots
}

/**
 * Flow layout — places items left-to-right, wrapping to the next row when
 * the current row would exceed the container width or optional item-count
 * limit. Each row's height is determined by the tallest item in that row.
 * Items are vertically centered within their row.
 */
export function computeFlowLayout(
  children: BinChildInfo[],
  containerWidth: number,
  gapOpenIndex?: number | null,
  draggedSize?: { width: number; height: number } | null,
  maxItemsPerRow?: number | null,
): BinLayoutResult {
  const positions = new Map<string, { x: number; y: number }>()
  const slots = buildSlotList(children, gapOpenIndex)
  const rowItemLimit = maxItemsPerRow == null ? Number.POSITIVE_INFINITY : Math.max(1, Math.round(maxItemsPerRow))

  // Gap-slot dimensions: prefer the dragged item's actual size so the
  // preview accurately reflects where the drop will land. Fall back to
  // children[0] for callers that don't pass dragged dimensions.
  const gapWidth = draggedSize?.width ?? children[0]?.width ?? 0
  const gapHeight = draggedSize?.height ?? children[0]?.height ?? 0

  const startY = BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING

  // First pass: assign items to rows based on wrapping
  const rows: { items: (BinChildInfo | null)[]; rowHeight: number }[] = []
  let currentRow: (BinChildInfo | null)[] = []
  let currentRowWidth = 0
  let currentRowHeight = 0

  for (const slot of slots) {
    const itemWidth = slot ? slot.width : gapWidth
    const itemHeight = slot ? slot.height : gapHeight

    // Grid callers cap the number of slots in a row as well as its physical
    // width. Without the count cap, a row of narrow cards can exceed the
    // chosen column count after a wider row establishes the container width.
    const rowIsFull = currentRow.length >= rowItemLimit
    const wouldOverflowWidth = currentRowWidth + BIN_ITEM_GAP + itemWidth > containerWidth
    if (currentRow.length > 0 && (rowIsFull || wouldOverflowWidth)) {
      rows.push({ items: currentRow, rowHeight: currentRowHeight })
      currentRow = []
      currentRowWidth = 0
      currentRowHeight = 0
    }

    currentRow.push(slot)
    currentRowWidth += (currentRow.length > 1 ? BIN_ITEM_GAP : 0) + itemWidth
    currentRowHeight = Math.max(currentRowHeight, itemHeight)
  }
  if (currentRow.length > 0) {
    rows.push({ items: currentRow, rowHeight: currentRowHeight })
  }

  // Second pass: compute positions, vertically centering items within each row
  let cursorY = startY
  let maxRowWidth = 0

  for (const row of rows) {
    let cursorX = BIN_CONTENT_PADDING
    for (const slot of row.items) {
      if (slot) {
        positions.set(slot.id, { x: cursorX, y: cursorY })
        cursorX += slot.width + BIN_ITEM_GAP
      } else {
        // Gap placeholder — advance cursor using the dragged item's width
        cursorX += gapWidth + BIN_ITEM_GAP
      }
    }
    maxRowWidth = Math.max(maxRowWidth, cursorX - BIN_ITEM_GAP)
    cursorY += row.rowHeight + BIN_ITEM_GAP
  }

  const totalWidth = Math.max(maxRowWidth + BIN_CONTENT_PADDING, containerWidth + BIN_CONTENT_PADDING * 2)
  const totalHeight = cursorY - BIN_ITEM_GAP + BIN_CONTENT_PADDING

  return { positions, width: totalWidth, height: totalHeight }
}

/**
 * Pick the column count that produces a grid bin whose aspect ratio is
 * closest to its children's aspect ratio.
 *
 * `round(sqrt(N))` ignores child aspect ratios entirely. For 16:9 cards it
 * lands well at perfect-square N (4, 9, 16) but at N=5,6,10,11,12 produces
 * a "two-column tall" feel where the bin's AR drifts toward portrait
 * (1.18 / 1.33) while the cards inside are landscape — visually awkward.
 *
 * Instead, evaluate every candidate `cols` and keep the one that minimises
 * `|log(bin_AR / target_AR)|` where `target_AR = avgW / avgH` (i.e. match
 * the items' own shape). `log` makes 2× and 0.5× deviations equally bad.
 * Average child dimensions handle heterogeneous bins (asset + audio + set).
 * Tiebreaker prefers more columns (wider bin) so near-ties never land on
 * the portrait choice — for landscape items, that produces a wider grid
 * for awkward Ns instead of a taller one.
 */
export function computeGridColCount(children: BinChildInfo[]): number {
  const N = children.length
  if (N <= 1) return Math.max(1, N)
  let avgW = 0
  let avgH = 0
  for (const c of children) {
    avgW += c.width
    avgH += c.height
  }
  avgW /= N
  avgH /= N
  if (avgW <= 0 || avgH <= 0) return Math.max(1, Math.round(Math.sqrt(N)))
  const targetLog = Math.log(avgW / avgH)
  let bestCols = 1
  let bestScore = Infinity
  for (let cols = 1; cols <= N; cols++) {
    const rows = Math.ceil(N / cols)
    const ar = (cols * avgW) / (rows * avgH)
    const score = Math.abs(Math.log(ar) - targetLog)
    if (score < bestScore - 1e-3 || (Math.abs(score - bestScore) < 1e-3 && cols > bestCols)) {
      bestScore = score
      bestCols = cols
    }
  }
  return bestCols
}

/**
 * Largest column count in `1..childCount` whose grid layout width fits
 * `availableWidth`. Turns a live-dragged bin width (corner-resize) into a
 * column count. Always returns at least 1 — a single column is the floor even
 * when it overflows the available width.
 *
 * ponytail: O(N²) — one `computeBinLayout` per candidate over N members. Fine
 * for the ≤ dozens of members a bin holds; if bins ever hold thousands,
 * binary-search the monotonic width instead.
 */
export function pickGridColumnsForWidth(children: BinChildInfo[], availableWidth: number): number {
  let best = 1
  for (let cols = 1; cols <= children.length; cols++) {
    const { width } = computeBinLayout(children, 'grid', null, null, cols)
    if (width <= availableWidth) best = cols
  }
  return best
}

/**
 * Grid layout — auto-columns chosen for closest-to-square bin shape, items
 * flow left-to-right and wrap to the next row. Handles mixed node sizes
 * gracefully.
 */
function computeGridLayout(
  children: BinChildInfo[],
  gapOpenIndex?: number | null,
  draggedSize?: { width: number; height: number } | null,
  forcedGridColumns?: number | null,
): BinLayoutResult {
  // A user-set column count (from corner-resizing the bin) wins over the
  // aspect-ratio heuristic. Clamp to 1..childCount so a stale count left over
  // from a since-shrunk bin can't blow the container width out past its items.
  // Otherwise derive from the settled child count only — including the gap
  // placeholder slot can push `cols` over a boundary the instant a gap opens,
  // jarringly re-flowing every item to a new row mid-drag.
  const forced = forcedGridColumns != null
  const cols = forced
    ? Math.max(1, Math.min(Math.round(forcedGridColumns), children.length))
    : computeGridColCount(children)
  // Size each intended row from its actual members. The old median-width
  // estimate could wrap mixed horizontal cards into uneven rows (for example
  // 2/3/1 with six cards) even though the chosen column count was three.
  let maxWindow = Math.max(...children.map(c => c.width))
  for (let i = 0; i < children.length; i += cols) {
    let sum = 0
    for (let j = i; j < Math.min(i + cols, children.length); j++) sum += children[j].width
    maxWindow = Math.max(maxWindow, sum)
  }
  const containerWidth = maxWindow + (cols - 1) * BIN_ITEM_GAP
  return computeFlowLayout(children, containerWidth, gapOpenIndex, draggedSize, cols)
}

// ============================================================================
// COLUMN LAYOUT
// ============================================================================

/**
 * Single vertical column — items placed top-to-bottom, horizontally
 * centered within the widest-item-sized track. Mirror of `computeRowLayout`.
 */
function computeColumnsLayout(
  children: BinChildInfo[],
  gapOpenIndex?: number | null,
  draggedSize?: { width: number; height: number } | null,
): BinLayoutResult {
  const positions = new Map<string, { x: number; y: number }>()
  const gapHeight = draggedSize?.height ?? children[0].height
  const gapWidth = draggedSize?.width ?? 0
  const maxW = Math.max(...children.map(c => c.width), gapOpenIndex != null ? gapWidth : 0)

  const startX = BIN_CONTENT_PADDING
  let cursorY = BIN_HEADER_HEIGHT + BIN_CONTENT_PADDING

  for (let i = 0; i < children.length; i++) {
    // Insert gap at the specified index, sized for the dragged item
    if (gapOpenIndex != null && i === gapOpenIndex) {
      cursorY += gapHeight + BIN_ITEM_GAP
    }

    const child = children[i]
    // Horizontally center within the column
    // Left-align within the column, mirror of the row's top alignment.
    positions.set(child.id, { x: startX, y: cursorY })
    cursorY += child.height + BIN_ITEM_GAP
  }

  // If gap is after the last item
  if (gapOpenIndex != null && gapOpenIndex >= children.length) {
    cursorY += gapHeight + BIN_ITEM_GAP
  }

  const totalHeight = cursorY - BIN_ITEM_GAP + BIN_CONTENT_PADDING
  const totalWidth = maxW + BIN_CONTENT_PADDING * 2

  return { positions, width: totalWidth, height: totalHeight }
}

// ============================================================================
// INSERTION INDEX DETECTION
// ============================================================================

/**
 * Compute which slot index a dragged item should be inserted at
 * based on the drag position within the bin.
 *
 * @param dragX - Drag position X relative to the bin node
 * @param dragY - Drag position Y relative to the bin node
 * @param children - Current children with their computed positions
 * @param layoutMode - The bin's layout mode
 * @returns The insertion index (0 to children.length)
 */
export function computeInsertionIndex(
  dragX: number,
  dragY: number,
  children: BinChildInfo[],
  positions: Map<string, { x: number; y: number }>,
  layoutMode: BinLayoutMode,
): number {
  if (children.length === 0) return 0

  // For row layout, only compare X positions
  if (layoutMode === 'row') {
    for (let i = 0; i < children.length; i++) {
      const pos = positions.get(children[i].id)
      if (!pos) continue
      const midX = pos.x + children[i].width / 2
      if (dragX < midX) return i
    }
    return children.length
  }

  // For single-column layout, only compare Y positions
  if (layoutMode === 'columns') {
    for (let i = 0; i < children.length; i++) {
      const pos = positions.get(children[i].id)
      if (!pos) continue
      const midY = pos.y + children[i].height / 2
      if (dragY < midY) return i
    }
    return children.length
  }

  // For grid (flow layout), find the closest midpoint
  // Walk through children in order; insert before the first child whose
  // midpoint the drag position is above or to the left of
  for (let i = 0; i < children.length; i++) {
    const pos = positions.get(children[i].id)
    if (!pos) continue
    const midX = pos.x + children[i].width / 2

    // If drag is above this row, insert here
    if (dragY < pos.y) return i
    // If on the same row (within height), check X
    if (dragY < pos.y + children[i].height && dragX < midX) return i
  }
  return children.length
}
